import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { EyeIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, EmptyState, Modal, RowAction, Table, Tag, Textarea } from "../../ui";
import { errorMessageOf, problemOf, scholarshipsApi } from "../../../api/scholarships";
import { SCHOLARSHIPS_KEY, isLoanType, needsSponsor, staffScholarshipKey } from "../model";
import { loanSummary } from "../funding";
import ApplicationDialog from "../review/ApplicationDialog";
import ApprovedFundingDialog from "../review/ApprovedFundingDialog";
import { REVIEW_KEY, formatDateTime, formatScore, fullName } from "../review/model";
import { HOLDER, LOANS_KEY, actionsFor, loanStatusOf, loansKey, rejectedByLabel, statusesFor, sendsToMinister, useLoans } from "./model";

const MAX_TEXT = 1000;
const STUDENT_RESPONSES = { PENDING: "Waiting for the student", ACCEPTED: "Student accepted", DECLINED: "Student declined" };

// One decision on the chosen loans: a rejection or a return needs a reason; what the DG sends to the Minister may carry a note
function DecisionDialog({ scholarship, decision, chosen, role, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const withNote = sendsToMinister(role, decision.action);

  const submit = async () => {
    setError(null);
    if (decision.reason && !reason.trim()) return setError("Say why.");
    setBusy(true);
    try {
      const { data } = await scholarshipsApi.post(`staff/scholarships/${scholarship.id}/loans/decisions`, {
        applicationIds: chosen.map((l) => l.applicationId),
        action: decision.action,
        ...(reason.trim() && { [withNote ? "note" : "reason"]: reason.trim() }),
      });
      toast.success(`${decision.label}: ${chosen.length === 1 ? fullName(chosen[0].applicant) : `${chosen.length} students`}`);
      onDone(data);
    } catch (err) {
      console.error(`Failed to ${decision.action} loans of scholarship ${scholarship.id}:`, err);
      setError(problemOf(err)?.code === "invalid-loan-action" ? "Some of the selected students are no longer with you. Reload and try again." : errorMessageOf(err, "Failed to save the decision. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      dismissible={!busy}
      title={`${decision.label}: ${chosen.length === 1 ? fullName(chosen[0].applicant) : `${chosen.length} students`}`}
      description={chosen.length > 1 ? chosen.map((l) => fullName(l.applicant)).join(", ") : null}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button size="sm" variant={decision.destructive ? "danger" : "solid"} onClick={submit} loading={busy}>
            {decision.label}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {(decision.reason || withNote) && (
          <Textarea
            label={decision.reason ? "Reason" : "Note for the Minister (optional)"}
            required={decision.reason}
            rows={3}
            maxLength={MAX_TEXT}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>
    </Modal>
  );
}

/**
 * A scholarship's awards and reserves on their way through the DG and the Minister, best HEC rank
 * first. `role` (DG, MINISTER or SCHOLARSHIP_ADMIN) decides which can be selected, which decisions
 * show, and, for a government loan, whose amounts can change.
 */
export default function LoansPanel({ scholarship, role }) {
  const queryClient = useQueryClient();
  const { data: loans = [], isPending, isError, refetch } = useLoans(scholarship.id);
  const [selected, setSelected] = useState([]); // application IDs
  const [deciding, setDeciding] = useState(null); // { decision, chosen }
  const [amountsFor, setAmountsFor] = useState(null); // the loan whose amounts are being changed
  const [savingAmounts, setSavingAmounts] = useState(false);
  const [openId, setOpenId] = useState(null);

  if (isError) return <EmptyState title="Could not load the approvals" onRefresh={refetch} />;

  const isLoan = isLoanType(scholarship.type);

  const mine = loans.filter((l) => actionsFor(role, l.status).length);
  const chosen = loans.filter((l) => selected.includes(l.applicationId));
  // The decisions every chosen loan allows
  const common = chosen.length ? actionsFor(role, chosen[0].status).filter((d) => chosen.every((l) => actionsFor(role, l.status).some((x) => x.action === d.action))) : [];
  const allSelected = mine.length > 0 && mine.every((l) => selected.includes(l.applicationId));
  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const counts = loans.reduce((acc, l) => ({ ...acc, [l.status]: (acc[l.status] ?? 0) + 1 }), {});

  const refresh = () => {
    // The scholarship becomes decided (or reopens), and HEC's award list shows where each loan stands
    queryClient.invalidateQueries({ queryKey: LOANS_KEY });
    queryClient.invalidateQueries({ queryKey: staffScholarshipKey(scholarship.id) });
    queryClient.invalidateQueries({ queryKey: [...SCHOLARSHIPS_KEY, "staff", "list"] });
    queryClient.invalidateQueries({ queryKey: REVIEW_KEY });
  };

  const decided = (fresh) => {
    queryClient.setQueryData(loansKey(scholarship.id), fresh);
    refresh();
    setSelected([]);
    setDeciding(null);
  };

  const saveAmounts = async (approvedFunding, reason) => {
    setSavingAmounts(true);
    try {
      await scholarshipsApi.put(`staff/applications/${amountsFor.applicationId}/loan-amounts`, { approvedFunding, ...(reason && { reason }) });
      toast.success(`Loan amounts saved for ${fullName(amountsFor.applicant)}`);
      refresh();
      setAmountsFor(null);
    } catch (err) {
      console.error(`Failed to change the loan amounts of application ${amountsFor.applicationId}:`, err);
      toast.error(errorMessageOf(err, "Failed to save the amounts. Please try again."));
    } finally {
      setSavingAmounts(false);
    }
  };

  const columns = [
    ...(mine.length
      ? [
          {
            key: "select",
            label: (
              <input
                type="checkbox"
                aria-label="Select every student you can decide on"
                checked={allSelected}
                onChange={() => setSelected(allSelected ? [] : mine.map((l) => l.applicationId))}
                className="h-4 w-4 accent-brand"
              />
            ),
            exportable: false,
            sortable: false,
            render: (l) =>
              actionsFor(role, l.status).length ? (
                <input
                  type="checkbox"
                  aria-label={`Select ${fullName(l.applicant)}`}
                  checked={selected.includes(l.applicationId)}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggle(l.applicationId)}
                  className="h-4 w-4 accent-brand"
                />
              ) : null,
          },
        ]
      : []),
    { key: "rank", label: "HEC rank", accessor: (l) => l.rank ?? "—", sortValue: (l) => l.rank ?? Infinity },
    { key: "applicant", label: "Student", accessor: (l) => fullName(l.applicant), render: (l) => <span className="font-medium text-slate-800">{fullName(l.applicant)}</span> },
    { key: "programme", label: "Programme", accessor: (l) => l.programme.name, filter: "Any programme" },
    { key: "meanScore", label: "HEC score", accessor: (l) => formatScore(l.meanScore), sortValue: (l) => l.meanScore },
    // Amounts belong to government loans only; other scholarships' awards carry none
    ...(isLoan
      ? [
          { key: "requested", label: "Requested", accessor: (l) => loanSummary(l.requestedFunding) },
          {
            key: "approved",
            label: "Approved",
            accessor: (l) => (l.approvedFunding ? loanSummary(l.approvedFunding) : "—"),
            render: (l) => (
              <span className="flex items-center gap-1">
                {l.approvedFunding ? loanSummary(l.approvedFunding) : "—"}
                {HOLDER[l.status]?.includes(role) && <RowAction Icon={PencilSquareIcon} label="Change amounts" onClick={() => setAmountsFor(l)} />}
              </span>
            ),
          },
        ]
      : []),
    {
      key: "status",
      label: "Status",
      accessor: (l) => loanStatusOf(l.status, isLoan).label,
      filter: "Any status",
      render: (l) => (
        <div className="flex flex-col gap-1">
          <Tag color={loanStatusOf(l.status, isLoan).color}>{loanStatusOf(l.status, isLoan).label}</Tag>
          {isLoan && l.status === "REJECTED" && <span className="text-xs text-slate-500">{rejectedByLabel(l.rejectedBy)}</span>}
          {l.promoted && l.status !== "RESERVE" && <span className="text-xs text-slate-500">Promoted from reserve</span>}
          {isLoan && l.studentResponse && <span className="text-xs text-slate-500">{STUDENT_RESPONSES[l.studentResponse]}{l.travelSubmittedAt ? " · travel details sent" : ""}</span>}
        </div>
      ),
    },
    {
      key: "comment",
      label: "Reason or note",
      accessor: (l) => l.reason ?? l.note ?? "",
      render: (l) => (l.reason || l.note ? <span className="line-clamp-2 max-w-xs text-sm italic text-slate-600">“{l.reason ?? l.note}”</span> : "—"),
    },
    { key: "updatedAt", label: "Since", accessor: (l) => (l.updatedAt ? formatDateTime(l.updatedAt) : "—"), sortValue: (l) => l.updatedAt ?? "" },
    { key: "view", label: "", exportable: false, render: (l) => <RowAction Icon={EyeIcon} label="View" onClick={() => setOpenId(l.applicationId)} /> },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap gap-2">
        {statusesFor(isLoan).map((s) => (
          <Tag key={s.value} color={s.color}>
            {s.label}: {counts[s.value] ?? 0}
          </Tag>
        ))}
      </div>
      <Table
        columns={columns}
        data={loans}
        onRowClick={(l) => setOpenId(l.applicationId)}
        isLoading={isPending}
        exportName={`approvals-${scholarship.name}`}
        emptyTitle="No awards sent for approval"
        actions={
          common.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {common.map((d) => (
                <Button key={d.action} size="sm" variant={d.destructive ? "danger-soft" : d.reason ? "outline" : "solid"} onClick={() => setDeciding({ decision: d, chosen })}>
                  {d.label} ({chosen.length})
                </Button>
              ))}
            </div>
          )
        }
      />
      {deciding && <DecisionDialog scholarship={scholarship} role={role} {...deciding} onClose={() => setDeciding(null)} onDone={decided} />}
      <ApprovedFundingDialog key={amountsFor?.applicationId} entry={amountsFor} withReason busy={savingAmounts} onApply={saveAmounts} onClose={() => setAmountsFor(null)} />
      <ApplicationDialog key={openId} applicationId={openId} programmes={scholarship.programmes} sponsored={needsSponsor(scholarship.type)} basePath="staff" onClose={() => setOpenId(null)} />
    </div>
  );
}
